import { Component } from "react";
import PropTypes from "prop-types";
import withStyles from "@mui/styles/withStyles";
import autoBind from "auto-bind/react";

import {
  TextField,
  Typography,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
} from "@mui/material";

import { validateEmail } from "../../../helpers/common";
import UserAccountActions from "./UserAccountActions";

import { isEqual } from "lodash";
import { SoftTitle } from "../../common/typography";
import { Check, Close, Warning } from "@mui/icons-material";
import DialogWrapper from "../../common/DialogWrapper";
const styles = theme => ({
  dialogContent: {
    padding: theme.spacing(2),
    paddingTop: theme.spacing(1),
  },
  input: {
    marginTop: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  workspacesContainer: {
    marginTop: theme.spacing(3),
  },
  fieldContainer: {
    position: "relative",
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
  },
  warningIcon: {
    color: theme.palette.warning.main,
    flexShrink: 0,
    fontSize: 34,
  },
});

class UserDialog extends Component {
  constructor(props) {
    super(props);
    autoBind(this);

    this.initialValues = {
      email: "",
      sysproContactId: "",
      givenName: "",
      familyName: "",
      nickname: "",
      positionUsername: "",
      positionUserGroup: "",
    };

    this.state = {
      values: {
        ...this.initialValues,
      },
      originalPositionUsername: "",
      errors: {},
      showAdminConfirmDialog: false,
      showDuplicateConfirmDialog: false,
      duplicateUsers: [],
    };
  }

  validate() {
    const requiredFields = ["email"];
    const { values } = this.state;
    const errors = {};

    requiredFields.forEach(field => {
      if (!values[field]) {
        errors[field] = "This field is required";
      }
    });

    if (!validateEmail(values.email)) {
      errors.email = "Invalid email format";
    }

    const isInnovasea = values.email && values.email.toLowerCase().endsWith("@innovasea.com");
    if (values.positionUserGroup === "admin" && !isInnovasea) {
      errors.positionUserGroup = "Only innovasea.com email addresses can be marked as admin";
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  }

  findDuplicateUsers() {
    const { values } = this.state;
    const { userToEdit, users } = this.props;

    // Only check if position username has a value
    if (!values.positionUsername || !users) {
      return [];
    }

    // Find all users with the same position username, excluding the current user being edited
    const duplicates = users.filter(
      user =>
        user.positionUsername === values.positionUsername && user.id !== (userToEdit?.id || null)
    );

    return duplicates;
  }

  hasDuplicateUsername() {
    const { values, originalPositionUsername } = this.state;
    // Only show warning if username has been changed from original
    if (values.positionUsername === originalPositionUsername) {
      return false;
    }
    return this.findDuplicateUsers().length > 0;
  }

  handleSubmit() {
    const isValid = this.validate();
    const { values } = this.state;

    if (!isValid) {
      return;
    }

    // Check if there are duplicate position usernames
    const hasDuplicate = this.hasDuplicateUsername();
    if (hasDuplicate) {
      const duplicateUsers = this.findDuplicateUsers();
      this.setState({
        showDuplicateConfirmDialog: true,
        duplicateUsers,
      });
      return;
    }

    // Check if user is being set to admin
    const isBecomingAdmin = values.positionUserGroup === "admin";
    const wasAdmin = this.props.userToEdit?.positionUserGroup === "admin";

    if (isBecomingAdmin && !wasAdmin) {
      // Show confirmation dialog for new admin assignment
      this.setState({ showAdminConfirmDialog: true });
      return;
    }

    // Proceed with save
    this.performSave();
  }

  performSave() {
    const { createUser, updateUser, userToEdit, mode } = this.props;
    const { values } = this.state;

    if (mode === "add") {
      createUser(values);
    } else if (mode === "edit") {
      const input = { id: userToEdit.id };
      userToEdit.email !== values.email && (input.email = values.email);
      userToEdit.givenName !== values.givenName && (input.givenName = values.givenName);
      userToEdit.familyName !== values.familyName && (input.familyName = values.familyName);
      userToEdit.nickname !== values.nickname && (input.nickname = values.nickname);
      userToEdit.sysproContactId !== values.sysproContactId &&
        (input.sysproContactId = values.sysproContactId);
      userToEdit.positionUsername !== values.positionUsername &&
        (input.positionUsername = values.positionUsername);
      userToEdit.positionUserGroup !== values.positionUserGroup &&
        (input.positionUserGroup = values.positionUserGroup);
      updateUser(input);
    }
    this.setState({
      values: this.initialValues,
      errors: {},
      showAdminConfirmDialog: false,
    });
  }

  confirmAdminAssignment() {
    this.setState({ showAdminConfirmDialog: false });
    this.performSave();
  }

  cancelAdminAssignment() {
    // Change position group back to "user" and close dialog
    this.setState({
      showAdminConfirmDialog: false,
      values: {
        ...this.state.values,
        positionUserGroup: "user",
      },
    });
  }

  confirmDuplicateSave() {
    this.setState({
      showDuplicateConfirmDialog: false,
      duplicateUsers: [],
    });
    this.performSave();
  }

  cancelDuplicateSave() {
    this.setState({
      showDuplicateConfirmDialog: false,
      duplicateUsers: [],
    });
  }

  handleInputChange(valueName, event) {
    this.setState({
      values: {
        ...this.state.values,
        [valueName]: event.target.value,
      },
    });
  }

  reset() {
    if (this.props.mode === "add") {
      this.setState({
        values: this.initialValues,
        originalPositionUsername: "",
        errors: {},
      });
    } else if (this.props.mode === "edit") {
      const originalUsername = this.props.userToEdit?.positionUsername || "";
      this.setState({
        values: {
          ...this.initialValues,
          ...this.props.userToEdit,
        },
        originalPositionUsername: originalUsername,
        errors: {},
      });
    }
  }

  render() {
    const { values, errors, showAdminConfirmDialog, showDuplicateConfirmDialog, duplicateUsers } =
      this.state;
    const { mode, handleClose, userToEdit, classes } = this.props;
    const editSubset = ({
      givenName,
      familyName,
      sysproContactId,
      nickname,
      email,
      positionUsername,
      positionUserGroup,
    }) => ({
      givenName,
      familyName,
      sysproContactId,
      nickname,
      email,
      positionUsername,
      positionUserGroup,
    });
    const editSubmitDisabled =
      mode === "edit" && isEqual(editSubset(values), editSubset(userToEdit));
    return (
      <DialogWrapper
        open={mode === "add" || mode === "edit"}
        title={mode === "add" ? "Add User" : "Edit User"}
        onEnter={this.reset}
        okAction={this.handleSubmit}
        okDisabled={editSubmitDisabled}
        okButtonContent={mode === "add" ? "Add" : "Save"}
        cancelAction={handleClose}
        maxWidth="lg"
        onBackdropClick={handleClose}
        fullWidth={false}
      >
        <div className={classes.dialogContent}>
          <Typography variant="h6">Details</Typography>
          <TextField
            size="small"
            className={classes.input}
            required
            fullWidth={true}
            label="Email"
            id="email"
            value={values.email || ""}
            onChange={event => this.handleInputChange("email", event)}
            error={errors.email ? true : false}
            helperText={
              errors.email
                ? errors.email
                : mode === "edit"
                  ? "Warning: Changing email will change their login email for Fathom Mobile & Fathom Live as well"
                  : ""
            }
          />
          <TextField
            size="small"
            className={classes.input}
            fullWidth={true}
            label="Syspro Contact Id"
            id="sysproContactId"
            value={values.sysproContactId || ""}
            onChange={event => this.handleInputChange("sysproContactId", event)}
            helperText={
              mode === "add" &&
              "If a syspro contact id is not provided we will attempt to lookup one by the email"
            }
          />
          <div style={{ display: "flex" }}>
            <TextField
              size="small"
              className={classes.input}
              label="Given Name"
              id="givenName"
              value={values.givenName || ""}
              onChange={event => this.handleInputChange("givenName", event)}
            />
            <TextField
              size="small"
              className={classes.input}
              label="Family Name"
              id="familyName"
              value={values.familyName || ""}
              onChange={event => this.handleInputChange("familyName", event)}
            />
            <TextField
              size="small"
              className={classes.input}
              label="Nickname"
              id="nickname"
              value={values.nickname || ""}
              onChange={event => this.handleInputChange("nickname", event)}
            />
          </div>
          {userToEdit && (
            <>
              <div style={{ display: "flex", flexDirection: "row", marginTop: 24 }}>
                <div style={{ marginRight: 16 }}>
                  <SoftTitle> Status </SoftTitle>
                  <Typography> {userToEdit.status} </Typography>
                </div>
                <div>
                  <SoftTitle> Enabled </SoftTitle>
                  <Typography> {userToEdit.enabled ? <Check /> : <Close />}</Typography>
                </div>
              </div>
              <div style={{ marginTop: 24 }}>
                <Typography variant="h6">Fathom Position</Typography>
                <div style={{ display: "flex" }}>
                  <div className={classes.fieldContainer}>
                    {this.hasDuplicateUsername() && (
                      <Warning
                        className={classes.warningIcon}
                        title="This value is used by other users"
                      />
                    )}
                    <TextField
                      size="small"
                      className={classes.input}
                      label="Username"
                      id="positionUsername"
                      value={values.positionUsername || ""}
                      onChange={event => this.handleInputChange("positionUsername", {
                        ...event,
                        target: { ...event.target, value: event.target.value.toLowerCase().replace(/[^a-z]/g, "") }
                      })}
                    />
                  </div>
                  <FormControl
                    size="small"
                    className={classes.input}
                    error={errors.positionUserGroup ? true : false}
                    style={{ minWidth: 200 }}
                  >
                    <InputLabel id="positionUserGroup-label">User Group</InputLabel>
                    <Select
                      labelId="positionUserGroup-label"
                      id="positionUserGroup"
                      value={values.positionUserGroup || ""}
                      onChange={event => this.handleInputChange("positionUserGroup", event)}
                      label="User Group"
                      displayEmpty
                    >
                      <MenuItem value="none">&lt;none&gt;</MenuItem>
                      <MenuItem value="user">user</MenuItem>
                      <MenuItem
                        value="admin"
                        disabled={
                          !values.email || !values.email.toLowerCase().endsWith("@innovasea.com")
                        }
                      >
                        admin
                      </MenuItem>
                    </Select>
                  </FormControl>
                  {errors.positionUserGroup && (
                    <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                      {errors.positionUserGroup}
                    </Typography>
                  )}
                </div>
              </div>
              <UserAccountActions user={userToEdit} />
            </>
          )}

          {mode === "add" && (
            <div className={classes.workspacesContainer}>
              <Typography>A personal workspace will be created automatically.</Typography>
            </div>
          )}
        </div>
        <Dialog open={showAdminConfirmDialog} onClose={() => this.cancelAdminAssignment()}>
          <DialogTitle>Confirm Admin Assignment</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to make this user an admin? Admins have elevated privileges in
              the system.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.cancelAdminAssignment()} color="primary">
              No, Make User
            </Button>
            <Button
              onClick={() => this.confirmAdminAssignment()}
              color="primary"
              variant="contained"
            >
              Yes, Make Admin
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={showDuplicateConfirmDialog} onClose={() => this.cancelDuplicateSave()}>
          <DialogTitle>Duplicate Position Username</DialogTitle>
          <DialogContent>
            <Typography>
              This position username is already used by the following user(s):
            </Typography>
            <ul style={{ marginTop: 12 }}>
              {duplicateUsers.map(user => (
                <li key={user.id}>
                  <Typography variant="body2">{user.email}</Typography>
                </li>
              ))}
            </ul>
            <Typography variant="body2" style={{ marginTop: 12 }}>
              Users can share a position username if they are from the same organization or group.
              Do you want to proceed?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.cancelDuplicateSave()} color="primary">
              Cancel
            </Button>
            <Button onClick={() => this.confirmDuplicateSave()} color="primary" variant="contained">
              Yes, Save Anyway
            </Button>
          </DialogActions>
        </Dialog>
      </DialogWrapper>
    );
  }
}

UserDialog.propTypes = {
  mode: PropTypes.oneOf(["add", "edit", ""]),
  handleClose: PropTypes.func,
  createUser: PropTypes.func,
  updateUser: PropTypes.func,
  userToEdit: PropTypes.shape({
    id: PropTypes.string,
    cognitoUuid: PropTypes.string,
    email: PropTypes.string,
    sysproContactId: PropTypes.string,
    positionUsername: PropTypes.string,
  }),
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      email: PropTypes.string,
      positionUsername: PropTypes.string,
    })
  ),
};

export default withStyles(styles)(UserDialog);
